import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { Loader2, Award, ExternalLink, Search, X, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const [y, m, d] = iso.split("-");
    return `${d}.${m}.${y}`;
  } catch {
    return iso;
  }
};

// Grund-Badge-Farbe je nach Status
const grundClass = (tn) => {
  if (tn.bestanden) return "bg-[#E9DDCB] text-[#5A3E2B] border border-[#D6C4A5]"; // Terra - bestanden
  if ((tn.grund || "").startsWith("Kurs beendet")) return "bg-[#F8F1E4] text-[#7A5A2E] border border-[#EBC9B9]";
  return "bg-stone-100 text-stone-700 border border-stone-200";
};

export default function FertigOhneZertifikat() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ total: 0, teilnehmer: [] });

  // Server-side Filter (an API übergeben)
  const [jahr, setJahr] = useState("2026");
  const [einschliesseAlt, setEinschliesseAlt] = useState(false);
  const [nurBestanden, setNurBestanden] = useState(false);

  // Client-side Filter
  const [search, setSearch] = useState("");
  const [einrichtungFilter, setEinrichtungFilter] = useState("all");
  const [kursFilter, setKursFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (jahr && jahr !== "alle") params.set("jahr", jahr);
      if (einschliesseAlt) params.set("einschliesse_alt", "true");
      if (nurBestanden) params.set("nur_bestanden", "true");
      const r = await axios.get(`${API}/teilnehmer/fertig-ohne-zertifikat?${params.toString()}`);
      setData(r.data || { total: 0, teilnehmer: [] });
    } catch (e) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  }, [jahr, einschliesseAlt, nurBestanden]);

  useEffect(() => { load(); }, [load]);

  // Client-side gefilterte Liste
  const gefiltert = useMemo(() => {
    const s = search.trim().toLowerCase();
    return (data.teilnehmer || []).filter(tn => {
      if (einrichtungFilter !== "all") {
        if (einrichtungFilter === "keine" && tn.pflegeeinrichtung_id) return false;
        if (einrichtungFilter !== "keine" && tn.pflegeeinrichtung_id !== einrichtungFilter) return false;
      }
      if (kursFilter !== "all" && (tn.kurs_name || "") !== kursFilter) return false;
      if (s) {
        const hay = `${tn.vorname} ${tn.nachname} ${tn.kurs_name} ${tn.einrichtung_name}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [data.teilnehmer, search, einrichtungFilter, kursFilter]);

  // Unique Optionen für Dropdowns aus dem aktuellen Datenset
  const einrichtungOptionen = useMemo(() => {
    const map = new Map();
    (data.teilnehmer || []).forEach(t => {
      if (t.pflegeeinrichtung_id && t.einrichtung_name) {
        map.set(t.pflegeeinrichtung_id, t.einrichtung_name);
      }
    });
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [data.teilnehmer]);

  const kursOptionen = useMemo(() => {
    const set = new Set();
    (data.teilnehmer || []).forEach(t => t.kurs_name && set.add(t.kurs_name));
    return Array.from(set).sort();
  }, [data.teilnehmer]);

  const filtersActive = search || einrichtungFilter !== "all" || kursFilter !== "all";

  const resetFilters = () => {
    setSearch("");
    setEinrichtungFilter("all");
    setKursFilter("all");
  };

  const goToTn = (id) => {
    window.location.href = `/teilnehmer?id=${id}`;
  };

  const exportCsv = () => {
    const cols = ["vorname", "nachname", "kurs_name", "kurs_start", "kurs_ende", "finanzierungsart", "bestanden", "einrichtung_name", "grund"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [cols.join(";"), ...gefiltert.map((tn) => cols.map((c) => esc(c === "bestanden" ? (tn.bestanden ? "ja" : "nein") : tn[c])).join(";"))];
    const url = URL.createObjectURL(new Blob(["\ufeff" + rows.join("\n")], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a"); a.href = url; a.download = `Fertig_ohne_Zertifikat_${jahr}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 space-y-6" data-testid="fertig-ohne-zert-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1F1B18] flex items-center gap-3">
            <Award size={26} className="text-[#B8410F]" />
            Fertig ohne Zertifikat
          </h1>
          <p className="text-sm text-stone-500 mt-1 max-w-3xl">
            Teilnehmer, die den Kurs abgeschlossen haben, aber noch kein Zertifikat besitzen.
            Standard: aktueller Jahrgang · Prüfung bestanden immer sichtbar · Alt-Ninox-Karteileichen ausgeblendet.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={exportCsv} data-testid="foz-export-csv">
            CSV exportieren
          </Button>
          <Button variant="outline" size="sm" onClick={load} data-testid="foz-reload">
            <RefreshCw size={14} className="mr-1.5" /> Neu laden
          </Button>
        </div>
      </div>

      {/* Filterleiste */}
      <Card className="border-stone-200">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[220px]">
              <Label className="text-xs text-stone-500">Suche</Label>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                <Input
                  placeholder="Name, Kurs oder Einrichtung..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="foz-search"
                />
              </div>
            </div>

            <div className="w-[130px]">
              <Label className="text-xs text-stone-500">Jahr</Label>
              <Select value={jahr} onValueChange={setJahr}>
                <SelectTrigger data-testid="foz-jahr"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="alle">Alle Jahre</SelectItem>
                  <SelectItem value="2026">2026</SelectItem>
                  <SelectItem value="2025">2025</SelectItem>
                  <SelectItem value="2024">2024</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="w-[220px]">
              <Label className="text-xs text-stone-500">Einrichtung</Label>
              <Select value={einrichtungFilter} onValueChange={setEinrichtungFilter}>
                <SelectTrigger data-testid="foz-einrichtung"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle</SelectItem>
                  <SelectItem value="keine">Ohne Einrichtung</SelectItem>
                  {einrichtungOptionen.map(([id, name]) => (
                    <SelectItem key={id} value={id}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="w-[220px]">
              <Label className="text-xs text-stone-500">Kurs</Label>
              <Select value={kursFilter} onValueChange={setKursFilter}>
                <SelectTrigger data-testid="foz-kurs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Kurse</SelectItem>
                  {kursOptionen.map(k => (
                    <SelectItem key={k} value={k}>{k}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label className="text-xs text-stone-500 flex items-center gap-2">
                <Switch
                  checked={nurBestanden}
                  onCheckedChange={setNurBestanden}
                  data-testid="foz-nur-bestanden"
                />
                <span>Nur Prüfung bestanden</span>
              </Label>
              <Label className="text-xs text-stone-500 flex items-center gap-2">
                <Switch
                  checked={einschliesseAlt}
                  onCheckedChange={setEinschliesseAlt}
                  data-testid="foz-alt-tn"
                />
                <span>Alt-TN einschließen</span>
              </Label>
            </div>

            {filtersActive && (
              <Button variant="ghost" size="sm" onClick={resetFilters} data-testid="foz-reset">
                <X size={13} className="mr-1" /> Zurücksetzen
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="border-stone-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center justify-between">
            <span>Kandidaten</span>
            <span className="text-sm font-normal text-stone-500" data-testid="foz-total">
              {loading ? (
                <Loader2 size={14} className="inline animate-spin" />
              ) : (
                <>
                  {gefiltert.length} von {data.total} Teilnehmer
                </>
              )}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-10 text-center text-stone-400"><Loader2 className="inline animate-spin" size={20} /></div>
          ) : gefiltert.length === 0 ? (
            <div className="py-10 text-center text-stone-500 text-sm px-4">
              {data.total === 0
                ? "Aktuell gibt es keine Teilnehmer, die fertig sind aber noch kein Zertifikat haben."
                : "Keine Treffer für die aktuellen Filter."}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[220px]">Teilnehmer</TableHead>
                  <TableHead>Kurs</TableHead>
                  <TableHead className="w-[110px]">Start</TableHead>
                  <TableHead className="w-[110px]">Ende</TableHead>
                  <TableHead className="w-[130px]">Letzte Anw.</TableHead>
                  <TableHead>Warum fertig?</TableHead>
                  <TableHead className="w-[100px] text-right">Aktion</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {gefiltert.map((tn) => (
                  <TableRow key={tn.id} className="hover:bg-[#faf6f1]" data-testid={`foz-row-${tn.id}`}>
                    <TableCell>
                      <div className="font-medium text-[#1F1B18]">{tn.vorname || "—"} {tn.nachname}</div>
                      <div className="flex flex-wrap gap-2 mt-0.5">
                        {tn.einrichtung_name && (
                          <span className="text-xs text-stone-500">{tn.einrichtung_name}</span>
                        )}
                        {tn.hat_ninox_id && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-stone-100 text-stone-500 border border-stone-200">Ninox-Import</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{tn.kurs_name}</TableCell>
                    <TableCell className="text-sm text-stone-600">{formatDate(tn.kurs_start)}</TableCell>
                    <TableCell className="text-sm text-stone-600">{formatDate(tn.kurs_ende)}</TableCell>
                    <TableCell className="text-sm text-stone-600">{formatDate(tn.letzte_anwesenheit)}</TableCell>
                    <TableCell>
                      <span className={`text-xs px-2 py-1 rounded-md ${grundClass(tn)}`}>
                        {tn.grund}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => goToTn(tn.id)}
                        data-testid={`foz-open-${tn.id}`}
                        title="Teilnehmerprofil öffnen und Zertifikat erstellen"
                      >
                        Öffnen <ExternalLink size={12} className="ml-1" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
